'use client';

import { useEffect, useState, useCallback, type ReactNode } from 'react';
import { ConfigProvider } from 'antd';
import zhCN from 'antd/es/locale/zh_CN';
import enUS from 'antd/es/locale/en_US';
import { initI18n, isI18nInitialized, getCurrentLanguage } from '@/sub-luca-plugin/i18n';
import { setGlobalFont } from '@/sub-luca-plugin/utils/i18n-font-setter';

interface I18nInitializerProps {
    children: ReactNode;
}

interface I18nState {
    isInitialized: boolean;
    themeToken: any;
    lang: string;
}

/**
 * I18nInitializer
 * 用于初始化本地 i18n 实例，确保子组件可以安全使用国际化能力。
 * 只在需要国际化的组件树中包裹一次即可。
 */
export function I18nInitializer({ children }: I18nInitializerProps) {
    const [state, setState] = useState<I18nState>({
        isInitialized: false,
        themeToken: null,
        lang: 'zh',
    });

    // 设置字体和主题的通用函数
    const setupFontAndTheme = useCallback((language: string) => {
        try {
            const { themeToken } = setGlobalFont(language);
            setState(prev => ({
                ...prev,
                themeToken,
                lang: language,
            }));
        } catch (error) {
            console.warn('字体设置失败:', error);
        }
    }, []);

    // 初始化 i18n 的函数
    const initializeI18n = useCallback(async () => {
        try {
            // 检查是否已经初始化
            if (isI18nInitialized()) {
                const lang = getCurrentLanguage();
                setupFontAndTheme(lang);
                setState(prev => ({
                    ...prev,
                    isInitialized: true,
                }));
                return;
            }

            // 初始化 i18n
            await initI18n();
            const lang = getCurrentLanguage();
            setupFontAndTheme(lang);

            setState(prev => ({
                ...prev,
                isInitialized: true,
            }));
        } catch (error) {
            console.error(`❌ Sub-luca-plugin failed to initialize i18n wrapper:`, error);
        }
    }, [setupFontAndTheme]);

    useEffect(() => {
        initializeI18n();
    }, [initializeI18n]);

    // 未初始化状态
    if (!state.isInitialized) {
        return null;
    }

    // 获取对应的 Ant Design 语言包
    const antdLocale = state.lang === 'en' ? enUS : zhCN;

    // 初始化完成，渲染子组件
    return (
        <ConfigProvider
            theme={{
                token: state.themeToken,
            }}
            locale={antdLocale}
        >
            {children}
        </ConfigProvider>
    );
}
